import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/hooks/use-theme";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  CalendarDays, ShieldCheck, Moon, Sun, LogOut, KeyRound, User, CalendarCheck2,
} from "lucide-react";

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [pwOpen, setPwOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const onAdmin = location.pathname.startsWith("/admin");

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 py-3">
          <button
            data-testid="logo-home-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 active:scale-95 transition-transform"
          >
            <span className="grid place-items-center h-9 w-9 rounded-xl overflow-hidden">
              <img src="/logo.png" alt="ParcCanin" className="h-9 w-9 object-contain" />
            </span>
            <span className="font-heading text-lg font-bold tracking-tight hidden sm:block">
              ParcCanin
            </span>
          </button>

          <nav className="flex items-center gap-1.5">
            <Button
              data-testid="nav-calendar-btn"
              variant={onAdmin ? "ghost" : "secondary"}
              size="sm"
              onClick={() => navigate("/")}
              className="gap-2"
            >
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">Calendrier</span>
            </Button>
            {isAdmin && (
              <Button
                data-testid="nav-admin-btn"
                variant={onAdmin ? "secondary" : "ghost"}
                size="sm"
                onClick={() => navigate("/admin")}
                className="gap-2"
              >
                <ShieldCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Administration</span>
              </Button>
            )}

            <div className="hidden md:flex flex-col items-end leading-tight ml-1">
              <span className="text-sm font-medium">
                {user?.first_name} {user?.last_name}
              </span>
              <span className="text-xs text-muted-foreground capitalize">{user?.role}</span>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="ghost" size="icon" className="rounded-xl border border-border">
                  <User className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="md:hidden">
                  {user?.first_name} {user?.last_name}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="md:hidden" />
                <DropdownMenuItem data-testid="menu-availability" onClick={() => navigate("/disponibilites")}>
                  <CalendarCheck2 className="h-4 w-4 mr-2" /> Disponibilités
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-theme" onClick={(e) => { e.preventDefault(); toggle(); }}>
                  {theme === "dark" ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                  Thème {theme === "dark" ? "clair" : "sombre"}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-password" onClick={() => setPwOpen(true)}>
                  <KeyRound className="h-4 w-4 mr-2" /> Mot de passe
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-10">{children}</main>
      <ChangePasswordModal open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
